<template>
  <b-modal
    v-model="showModal"
    :title="isEditMode ? 'Update Product Discount' : 'Add Product Discount'"
    size="lg"
    class="m-4"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <div
      v-if="initialLoading"
      class="text-center py-3"
    >
      <b-spinner
        label="Loading..."
        variant="primary"
      />
    </div>

    <b-form
      v-else
      @submit.prevent="submitHandler"
    >
      <b-row>
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>
                {{ errorMessage }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>
                {{ errorMessage.detail }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.non_field_errors"
              class="alert-body"
            >
              <p>
                {{ errorMessage.non_field_errors[0] }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>
                {{ errorMessage.message }}
              </p>
            </div>
          </b-alert>
        </b-col>

        <!-- Product Selection -->
        <b-col cols="6">
          <b-form-group>
            <template #label>
              Product
              <span class="text-danger">*</span>
            </template>
            <custom-v-select
              v-model="selectedProduct"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="filteredProductOptions || []"
              :loading="productOptionsLoading"
              :class="{ 'error-border': validationErrors.product }"
              label="display_text"
              placeholder="Search product..."
              :filterable="false"
              @search="searchProducts"
              @input="handleProductChange"
              @open="onProductDropdownOpen"
            />
            <p
              v-if="validationErrors.product"
              class="error-message"
            >
              {{ validationErrors.product }}
            </p>
            <p
              v-if="selectedProduct && (selectedProduct.company || selectedProduct.company_discount)"
              class="mb-0 mt-25"
            >
              <span class="text-primary font-weight-bold">{{ selectedProduct.company }}</span>
              <span> : </span>
              <span class="text-success font-weight-bold">{{ selectedProduct.company_discount }}%</span>
            </p>
          </b-form-group>
        </b-col>
        <!-- Discount -->
        <b-col cols="3">
          <b-form-group>
            <template #label>
              Discount (%)
              <span class="text-danger">*</span>
            </template>
            <b-form-input
              v-model="discount"
              type="number"
              min="0"
              max="100"
              step="0.01"
              placeholder="Enter discount %"
              :class="{ 'error-border': validationErrors.discount }"
              @input="clearValidationError('discount')"
            />
            <p
              v-if="validationErrors.discount"
              class="error-message"
            >
              {{ validationErrors.discount }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Active Status Switch -->
        <b-col cols="3">
          <b-form-group label="status">
            <div class="d-flex align-items-center">
              <b-form-checkbox
                v-model="isActive"
                switch
                label-class="d-none"
                size="lg"
              >
                <span style="font-size: 1.8rem;">
                  {{ isActive ? 'Active' : 'Inactive' }}
                </span>
              </b-form-checkbox>
            </div>
          </b-form-group>
        </b-col>

        <!-- Batch Selection -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Batches
            </template>
            <custom-v-select
              v-model="selectedBatches"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="batchOptions"
              :loading="batchLoading"
              :class="{ 'error-border': validationErrors.batches }"
              label="text"
              multiple
              placeholder="Select Batches"
              @input="handleBatchChange"
            />
            <p
              v-if="validationErrors.batches"
              class="error-message"
            >
              {{ validationErrors.batches }}
            </p>
            <p
              v-if="batchLoadingError"
              class="error-message"
            >
              {{ batchLoadingError }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Start Date -->
        <b-col cols="6">
          <b-form-group
            label="Start Date"
            label-for="start-date"
          >
            <b-form-datepicker
              id="start-date"
              v-model="startDate"
              menu-class="w-100"
              calendar-width="100%"
              :class="{ 'error-border': validationErrors.startDate }"
              reset-button
              @input="handleStartDateChange"
            />
            <p
              v-if="validationErrors.startDate"
              class="error-message"
            >
              {{ validationErrors.startDate }}
            </p>
            <p
              v-if="errorMessage && errorMessage.start_date"
              class="error-message"
            >
              {{ errorMessage.start_date[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- End Date -->
        <b-col cols="6">
          <b-form-group
            label="End Date"
            label-for="end-date"
          >
            <b-form-datepicker
              id="end-date"
              v-model="endDate"
              menu-class="w-100"
              calendar-width="100%"
              :min="startDate"
              :class="{ 'error-border': validationErrors.endDate }"
              reset-button
            />
            <p
              v-if="validationErrors.endDate"
              class="error-message"
            >
              {{ validationErrors.endDate }}
            </p>
            <p
              v-if="errorMessage && errorMessage.end_date"
              class="error-message"
            >
              {{ errorMessage.end_date[0] }}
            </p>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving"
        @click="ok()"
      >
        {{ isEditMode ? 'Update Discount' : 'Create Discount' }}
        <b-spinner
          v-if="saving"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput,
  BForm, BSpinner, BAlert, BFormDatepicker, BFormCheckbox,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getParsedQueryString from '@/utils/headerQueryParser'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BAlert,
    BFormDatepicker,
    BFormCheckbox,
  },

  props: {
    editData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      showModal: true,
      initialLoading: false,
      selectedProduct: null,
      productOptions: [],
      filteredProductOptions: [],
      productOptionsLoading: false,
      discount: '',
      isActive: true,
      selectedBatches: [],
      batchOptions: [],
      batchLoading: false,
      batchLoadingError: null,
      startDate: null,
      endDate: null,
      saving: false,
      errorMessage: {},
      validationErrors: {},
      searchTimeout: null,
      // Tracks original linked product ids to compute unlink_product_ids on update
      originalLinkedProductIds: [],
      // Accumulates ids that must be unlinked (includes batches lost on product change)
      forceUnlinkProductIds: [],
    }
  },

  computed: {
    isEditMode() {
      return !!this.editData
    },
  },

  created() {
    if (this.isEditMode) {
      this.initEditMode()
    } else {
      this.fetchProductOptions()
    }
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },

  methods: {
    async initEditMode() {
      this.initialLoading = true

      try {
        await this.fetchProductOptions()

        // Match and set product from editData
        const matchedProduct = this.productOptions.find(
          p => p.code === this.editData.product_code,
        )
        if (matchedProduct) {
          this.selectedProduct = matchedProduct
          this.filteredProductOptions = [
            matchedProduct,
            ...this.filteredProductOptions.filter(p => p.code !== matchedProduct.code),
          ]
        } else {
          this.selectedProduct = {
            id: this.editData.product_code,
            code: this.editData.product_code,
            name: this.editData.product_name,
            display_text: this.editData.product_name,
            company_discount: this.editData.company_discount,
            company: this.editData.company,
          }
          this.filteredProductOptions = [this.selectedProduct, ...this.filteredProductOptions]
        }

        // Fetch batch options for the product
        if (this.editData.product_code) {
          await this.fetchBatchOptions(this.editData.product_code)
        }

        // Store original linked ids and pre-select matching batches
        if (this.editData.linked_products && this.editData.linked_products.length > 0) {
          this.originalLinkedProductIds = this.editData.linked_products.map(lp => lp.id)
          this.selectedBatches = this.batchOptions.filter(
            batch => this.originalLinkedProductIds.includes(batch.id),
          )
        }

        // Populate remaining fields
        this.discount = this.editData.discount || ''
        this.isActive = this.editData.is_active !== undefined ? this.editData.is_active : true
        this.startDate = this.editData.start_date || null
        this.endDate = this.editData.end_date || null
      } catch {
        this.errorMessage = 'Failed to load edit data'
      } finally {
        this.initialLoading = false
      }
    },

    /**
     * Returns all ids that need to be sent as unlink_product_ids:
     * - Batches from forceUnlinkProductIds (accumulated when product was changed)
     * - Original batches that were manually deselected
     */
    getUnlinkedProductIds() {
      const currentSelectedIds = this.selectedBatches.map(b => b.id)
      const deselectedIds = this.originalLinkedProductIds.filter(
        id => !currentSelectedIds.includes(id),
      )

      // Merge force-unlinked + deselected, deduplicate
      const allUnlinked = [...new Set([...this.forceUnlinkProductIds, ...deselectedIds])]
      return allUnlinked
    },

    async fetchProductOptions() {
      this.productOptionsLoading = true
      try {
        const productRes = await axios.get('/marg/product_options')
        this.productOptions = productRes.data.data.map(product => ({
          id: product.code,
          code: product.code,
          name: product.name,
          display_text: `${product.name}`,
          company_discount: parseFloat(product.company_discount),
          company: product.company,
        }))
        this.filteredProductOptions = this.productOptions.slice(0, 15)
        this.productOptionsLoading = false
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || 'Failed to load products'
        this.productOptionsLoading = false
      }
    },

    onProductDropdownOpen() {
      if (!this.filteredProductOptions.length && this.productOptions.length) {
        this.filteredProductOptions = this.productOptions.slice(0, 15)
      }
    },

    searchProducts(query) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        if (!query || query.trim() === '') {
          this.filteredProductOptions = this.productOptions.slice(0, 15)
        } else {
          const searchTerm = query.toLowerCase()
          const filtered = this.productOptions.filter(product => product.name.toLowerCase().includes(searchTerm))
          this.filteredProductOptions = filtered.slice(0, 50)
        }
      }, 200)
    },

    validateForm() {
      this.validationErrors = {}
      let isValid = true

      if (!this.selectedProduct) {
        this.validationErrors.product = 'Product is required'
        isValid = false
      }

      if (this.discount === '' || this.discount === null || this.discount === undefined) {
        this.validationErrors.discount = 'Discount is required'
        isValid = false
      } else {
        const discountValue = parseFloat(this.discount)
        if (Number.isNaN(discountValue) || discountValue < 0) {
          this.validationErrors.discount = 'Discount cannot be negative'
          isValid = false
        } else if (discountValue > 100) {
          this.validationErrors.discount = 'Discount cannot exceed 100%'
          isValid = false
        }
      }

      if (!(this.selectedBatches.length > 0 || this.startDate)) {
        this.validationErrors.batches = 'At least one batch or Start Date is required'
        this.validationErrors.startDate = 'Start Date or at least one batch is required'
        isValid = false
      }

      return isValid
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field)
      }
    },

    handleStartDateChange() {
      this.clearValidationError('startDate')
      this.clearValidationError('batches')
    },

    handleProductChange(product) {
      if (this.validationErrors.product) {
        this.$delete(this.validationErrors, 'product')
      }

      // In edit mode: when product changes, all currently selected batches
      // plus any remaining original batches must be unlinked
      if (this.isEditMode) {
        const currentSelectedIds = this.selectedBatches.map(b => b.id)
        const allToUnlink = [...new Set([...this.originalLinkedProductIds, ...currentSelectedIds])]
        allToUnlink.forEach(id => {
          if (!this.forceUnlinkProductIds.includes(id)) {
            this.forceUnlinkProductIds.push(id)
          }
        })
        // Reset original tracking since we've moved all to force-unlink
        this.originalLinkedProductIds = []
      }

      this.selectedBatches = []
      this.batchOptions = []
      this.batchLoadingError = null

      if (product && product.code) {
        this.fetchBatchOptions(product.code)
      }
    },

    async fetchBatchOptions(productCode) {
      const params = { code: productCode }

      this.batchLoading = true
      this.batchLoadingError = null

      try {
        const res = await axios.get(`/marg/get_erp_batches?${getParsedQueryString(params)}`)
        const batchOptions = res.data.batches.reverse().map(batch => ({
          id: batch.id,
          text: batch.batch,
          batch_number: batch.batch_number,
          expiry_date: batch.expiry_date,
          special_bonus: batch.special_bonus,
        }))
        this.batchOptions = batchOptions
        this.batchLoading = false
      } catch (error) {
        this.batchLoadingError = error?.response?.data?.error || 'Something went wrong'
        this.batchLoading = false
      }
    },

    handleBatchChange() {
      if (this.validationErrors.batches) {
        this.$delete(this.validationErrors, 'batches')
      }
      if (this.validationErrors.startDate) {
        this.$delete(this.validationErrors, 'startDate')
      }
    },

    async submitHandler(event) {
      event.preventDefault()

      if (!this.validateForm()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please fix the errors and try again',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      this.saving = true

      try {
        const params = new URLSearchParams()

        params.append('product_code', this.selectedProduct.code)
        params.append('discount', this.discount)
        params.append('is_active', this.isActive)

        if (this.selectedBatches.length > 0) {
          this.selectedBatches.forEach(batch => {
            params.append('erp_product_ids', batch.id)
          })
        }

        // In edit mode, send unlink_product_ids for all removed batches
        if (this.isEditMode) {
          const unlinkedIds = this.getUnlinkedProductIds()
          unlinkedIds.forEach(id => {
            params.append('unlink_product_ids', id)
          })
        }

        if (this.startDate) {
          params.append('start_date', this.startDate)
        }

        if (this.endDate) {
          params.append('end_date', this.endDate)
        }

        const requestConfig = {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
          },
        }

        if (this.isEditMode) {
          await axios.patch(`/marg/product-discounts/${this.editData.id}/`, params, requestConfig)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Product Discount Updated Successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('item-discount-updated')
        } else {
          await axios.post('/marg/product-discounts/create/', params, requestConfig)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Product Discount Created Successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('item-discount-added')
        }

        this.saving = false
      } catch (error) {
        this.errorMessage = error?.response?.data?.product_code?.[0] || error?.response?.data || 'Something went wrong'
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.error-border {
  border-color: #dc3545 !important;
}

.error-border:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.text-danger {
  color: #dc3545 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.custom-switch::v-deep .custom-control-label {
  padding-left: 0.25rem;
}

input::placeholder,
textarea::placeholder {
  color: #6c757d !important;
  opacity: 1;
}

.vs__dropdown-toggle .vs__selected-options .vs__search::placeholder {
  color: #6c757d !important;
}

.vs__dropdown-menu .vs__dropdown-option--disabled {
  background-color: #ffebee !important;
  color: #c62828 !important;
  cursor: not-allowed !important;
}

.vs__dropdown-menu .vs__dropdown-option--disabled:hover {
  background-color: #ffcdd2 !important;
  color: #b71c1c !important;
}
</style>
